import { Directive, ElementRef, HostListener, Input, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ValueFormatterService, ValueMask } from '@shared/value-formatter/value-formatter.service';

type ValueType = string | number;

@UntilDestroy()
@Directive({
    selector: 'input[waiMask]',
    standalone: false
})
export class FormInputMaskDirective implements ControlValueAccessor {
  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl,
    private valueFormatter: ValueFormatterService,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @Input()
  public set value(value: ValueType) {
    if (this.isEditing) {
      // when editing, pass whatever is on input
      this.elementRef.nativeElement.value = value?.toString();
    } else {
      // when not editing, use clean and formatted values
      this.format(value);
      this._value = this.rawValue;
      this.elementRef.nativeElement.value = this.formattedValue;
    }
  }

  public get value(): ValueType {
    return this._value;
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('waiMask') public mask: ValueMask = 'decimal';

  private isEditing = false;

  private rawValue = '';

  private formattedValue = '';

  private _value: ValueType = null;

  public onChange: (value: ValueType) => void = () => {};

  public onTouched: (value: ValueType) => void = () => {};

  /**
   * @internal
   * Used in tests
   */
  public getRawValue(): FormInputMaskDirective['rawValue'] {
    return this.rawValue;
  }

  public writeValue(value: ValueType): void {
    // Determine if this mask type should use numeric values
    const isNumericMask = this.mask === 'currency' || this.mask === 'decimal' || this.mask === 'integer' || this.mask === 'percent';
    
    if (isNumericMask) {
      // For numeric masks, convert to number
      // For percent, also remove the % symbol if present
      if (typeof value === 'number') {
        this._value = value;
      } else {
        const cleanedValue = value ? value.toString().replace(/[^\d.,-]/g, '').replace(',', '.') : null;
        const numericValue = cleanedValue ? parseFloat(cleanedValue) : null;
        this._value = isNaN(numericValue) ? null : numericValue;
      }
    } else {
      // For non-numeric masks (phone, postalCode, etc.), keep as string
      this._value = value != null ? value.toString() : null;
    }
    
    // If we have a valid value, format it for display
    if (this._value != null) {
      // Always ensure rawValue is set first (before formatting)
      this.rawValue = this._value.toString();
      
      // Format the value for display
      try {
        this.format(this._value.toString());
      } catch (error) {
        // Fallback: use raw value if formatting fails
        this.formattedValue = '';
      }
      
      // Determine what to display: formatted value if available and not empty, otherwise raw value
      const displayValue = (this.formattedValue && this.formattedValue.trim() !== '') 
        ? this.formattedValue 
        : this.rawValue;
      
      // Always update the input element - this ensures the value is visible on page load
      this.elementRef.nativeElement.value = displayValue;
    } else {
      // Value is null/undefined - clear the input
      this.elementRef.nativeElement.value = '';
      this.rawValue = '';
      this.formattedValue = '';
    }
  }

  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  @HostListener('blur')
  public onBlur(): void {
    this.isEditing = false;
    // Format the current value for display
    const valueToFormat = this._value != null ? this._value : (this.elementRef.nativeElement.value || null);
    this.format(valueToFormat);
    this.elementRef.nativeElement.value = this.formattedValue;
    
    if (this.onChange) {
      this.onChange(this._value);
    }
    if (this.onTouched) {
      this.onTouched(this._value);
    }

    if (this.ngControl) {
      // so errors can be displayed
      this.ngControl.control.markAsTouched();
    }
  }

  @HostListener('focus')
  public onFocus(): void {
    this.isEditing = true;
    // Use rawValue if available, otherwise convert _value to string for editing
    const displayValue = this.rawValue || (this._value != null ? this._value.toString() : '');
    this.elementRef.nativeElement.value = displayValue;
  }

  @HostListener('input', ['$event.target.value'])
  public onInput(value: string): void {
    try {
      // Determine if this mask type should use numeric values
      const isNumericMask = this.mask === 'currency' || this.mask === 'decimal' || this.mask === 'integer' || this.mask === 'percent';
      
      if (isNumericMask) {
        // For numeric masks, strip non-digit chars and convert to number
        // For percent, also remove the % symbol if present
        const cleanedValue = value?.toString()?.replace(/[^\d.,-]/g, '').replace(',', '.');
        const numericValue = cleanedValue === '' || cleanedValue === null ? null : parseFloat(cleanedValue);
        const sanitizedValue = isNaN(numericValue) ? null : numericValue;

        this._value = sanitizedValue;
        // Keep rawValue in sync so onFocus can display it correctly
        this.rawValue = cleanedValue || '';
      } else {
        // For non-numeric masks, keep as string
        this._value = value || null;
        this.rawValue = value || '';
      }

      // Update validators with the sanitized value
      if (this.onChange) {
        this.onChange(this._value);
      }
    } catch (error) {
      // Fallback: set to null on any parsing error
      this._value = null;
      this.rawValue = '';
      if (this.onChange) {
        this.onChange(null);
      }
    }
  }

  private format(value: ValueType): void {
    const { rawValue, formattedValue } = this.valueFormatter.format(this.mask, value);
    this.rawValue = rawValue;
    this.formattedValue = formattedValue;
  }
}
